import { format, isValid, parse, parseISO } from 'date-fns'

import { DateFormatError, HUMAN_DATE_FORMAT, ISO_LOCAL_DATE_FORMAT } from '@Constants/date'

// Inspired by https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s07.html
const HUMAN_DATE_REGEX = /^([0-2]\d|(3)[0-1])(\/)(((0)\d)|((1)[0-2]))(\/)\d{4}$/

// Inspired by https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s07.html
const ISO_LOCAL_DATE_REGEX = /^(\d{4})(-)(1[0-2]|0[1-9])\2(3[01]|0[1-9]|[12]\d)$/

// Inspired by https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s07.html
const ISO_LOCAL_DATE_TIME_REGEX =
  /^(-?(?:[1-9]\d*)?\d{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12]\d)T(2[0-3]|[01]\d):([0-5]\d):([0-5]\d)(\.\d+)?(Z|[+-](?:2[0-3]|[01]\d):[0-5]\d)?$/

export const hasHumanDateFormat = (dateStr: string): boolean => HUMAN_DATE_REGEX.test(dateStr)

/**
 * Only determine if a string is in date-only form ("2011-10-10")
 * @param dateStr
 */
export const hasLocalDateFormat = (dateStr: string): boolean => ISO_LOCAL_DATE_REGEX.test(dateStr)

/**
 * Only determine if a string is in date-time form ("2011-10-10T14:48:00") or date-time form with milliseconds and time zone ("2011-10-10T14:48:00.000+09:00")
 * @param dateStr
 */
export const hasLocalDateTimeFormat = (dateStr: string): boolean => ISO_LOCAL_DATE_TIME_REGEX.test(dateStr)

export const parseDateToISOLocalDate = (date: Date): string => format(date, ISO_LOCAL_DATE_FORMAT)

export const parseDateToISOHumanDate = (date: Date): string => format(date, HUMAN_DATE_FORMAT)

export const parseHumanDateToISOLocalDate = (dateStr: string): string => {
  if (!hasHumanDateFormat(dateStr)) {
    return DateFormatError.INVALID_HUMAN_DATE
  }

  const date = parse(dateStr, HUMAN_DATE_FORMAT, new Date())

  if (!isValid(date)) {
    return DateFormatError.INVALID_HUMAN_DATE
  }

  return parseDateToISOLocalDate(date)
}

export const parseISOToHumanDate = (dateStr: string): string => {
  if (!hasLocalDateTimeFormat(dateStr) && !hasLocalDateFormat(dateStr)) {
    return DateFormatError.INVALID_ISO_DATE
  }

  const date = parseISO(dateStr)

  if (!isValid(date)) {
    return DateFormatError.INVALID_ISO_DATE
  }

  return format(date, HUMAN_DATE_FORMAT)
}

export const parseISOToISOLocalDate = (dateStr: string): string => {
  if (!hasLocalDateTimeFormat(dateStr) && !hasLocalDateFormat(dateStr)) {
    return DateFormatError.INVALID_ISO_DATE
  }

  const date = parseISO(dateStr)

  if (!isValid(date)) {
    return DateFormatError.INVALID_ISO_DATE
  }

  return parseDateToISOLocalDate(date)
}

export const parseLocalDateToISO = (dateStr: string): string => {
  if (!hasLocalDateFormat(dateStr)) {
    return DateFormatError.INVALID_ISO_LOCAL_DATE
  }

  const date = parseISO(dateStr)

  if (!isValid(date)) {
    return DateFormatError.INVALID_ISO_LOCAL_DATE
  }

  return date.toISOString()
}
